import { data } from "react-router";
import { SITE_URL } from "~/lib/seo";
import { slugify } from "~/lib/slug";

/** Notice shown instead of a name for GDPR-redacted persons (kurzy.cz convention). */
export const PERSON_REDACTED_NOTICE = "Subjekt byl na svou žádost vymazán";

/**
 * Person ids are UUIDs (`Person.id @default(uuid())` in the API Prisma schema),
 * so they contain dashes and a naive `idSlug.split("-")[0]` would truncate them.
 * The canonical URL is `/osoba/{uuid}-{slug}`; we anchor-match the fixed-length
 * UUID (8-4-4-4-12 hex groups) at the start of the param and treat everything
 * after the following dash as the (ignorable) slug. The hex groups have fixed
 * lengths, so the id/slug boundary is unambiguous and cannot misparse.
 */
const PERSON_ID_SLUG_RE =
	/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:-(.*))?$/;

/** Extracts the person UUID (and trailing slug, if any) from a `{id}-{slug}` URL param. Throws 404 when malformed. */
export function parsePersonIdSlug(idSlug: string): { personId: string; slug: string | null } {
	const match = idSlug.match(PERSON_ID_SLUG_RE);
	if (!match) throw data(null, { status: 404 });
	// Prisma generates lowercase UUIDs — normalize so pasted uppercase ids still resolve.
	return { personId: match[1].toLowerCase(), slug: match[2] ?? null };
}

/** Canonical app-relative path of a person profile: `/osoba/{id}-{slug(name)}`. */
export function personPath(personId: string, name: string): string {
	const slug = slugify(name);
	return slug ? `/osoba/${personId}-${slug}` : `/osoba/${personId}`;
}

/** Canonical absolute URL of a person profile. */
export function personCanonicalUrl(personId: string, name: string): string {
	return `${SITE_URL}${personPath(personId, name)}`;
}
